import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { UsersApi } from 'api/UsersApi';
import type { UserWithType } from 'domain/types';
import { getAppViewStateStore } from 'stores/AppViewStateStore';
import { getUserStore } from 'stores/UserStore';
import { getUserType } from 'ui/routing/router-util';
import { USER_KEY } from './consts';

export function useUser() {
  const navigate = useNavigate();
  const usersApi = new UsersApi();
  const appViewStore = getAppViewStateStore();
  const userStore = getUserStore();
  const userId = localStorage.getItem('userId') || '';

  const {
    data: user,
    isLoading: isLoadingUser,
    isError: isUserError,
    isSuccess: isUserSuccess
  } = useQuery({
    enabled: appViewStore.isReadyToLoadUser && !!userId.length,
    queryKey: [USER_KEY, userId],
    queryFn: async () => {
      const data = await usersApi.getUser(userId);
      return { ...data, type: getUserType(data) } as UserWithType;
    },
    meta: {
      snackbarErrorMessage: 'There was an issue logging in. Please contact support if the problem persists.'
    }
  });

  const userBankId = useMemo(() => {
    return user?.companies && user?.type === 'bank' ? user?.companies?.[0].id : '';
  }, [user?.companies?.[0]?.id, user?.type]);

  useEffect(() => {
    if (isUserSuccess && user?.id) {
      userStore.updateUser(user);
    }
  }, [isUserSuccess, user]);

  useEffect(() => {
    if (isUserError) {
      navigate('/error');
    }
  }, [isUserError]);

  return { user, userBankId, isLoadingUser, isUserError };
}
